'use client';

import React, { useMemo } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { DebugInfo } from '@/app/components/search/components/Banners/Debug/styled.ts';
import { type DebugValue, parseDebugValueNames, resolveDebugValues } from '@/app/common/debug/debugValues.ts';

/**
 * The `debug_values` toggle, read off the page the viewer is actually on.
 *
 * Empty list means the toggle is off, which is what every caller gates on — so this doubles as the
 * "is the debug tooling switched on" test and there is no second flag to keep in step. (MPH-1086)
 */
export const useDebugValues = (): DebugValue[] => {
  const { commonData, localization } = usePageWrapperData();
  const raw = localization?.configValuesApp?.debugValues;
  const abTests = commonData?.abTests;
  const configValues = commonData?.configValues;

  return useMemo(() => {
    const names = parseDebugValueNames(raw);
    if (names.length === 0) return [];
    return resolveDebugValues({ names, abTests, configValues });
  }, [raw, abTests, configValues]);
};

/** One line per listed name. Renders nothing at all when the toggle is off. */
export const DebugValuesLines = () => {
  const debugValues = useDebugValues();
  if (debugValues.length === 0) return null;

  return (
    <>
      {debugValues.map(({ name, source, value }) => (
        <DebugInfo key={name}>
          {name} ({source}): <strong>{value}</strong>
        </DebugInfo>
      ))}
    </>
  );
};
